<template>
    <!-- full time: the MVP of the match -->
    <broadcast-card v-if="mvp" eyebrow="Τέλος αγώνα" title="MVP του αγώνα">
        <div class="mvp" :style="{ '--team-color': mvpSide.color }">
            <div class="mvp__photo">
                <img v-if="mvpSide.logo" :src="mvpSide.logo" alt="" class="mvp__watermark">
                <img :src="playerImage(mvpPlayer)" alt="" class="mvp__img" @error="$event.target.src = defaultPlayerImg">
            </div>
            <div class="mvp__info">
                <span class="mvp__team">
                    <img v-if="mvpSide.logo" :src="mvpSide.logo" alt="">
                    {{ mvpSide.name }}
                </span>
                <span class="mvp__name">{{ playerName(mvpPlayer) }}</span>
                <span class="mvp__pir">{{ mvpStats.pir || 0 }} <small>PIR</small></span>
                <div class="mvp__tiles">
                    <span v-for="tile in mvpTiles" :key="tile.label" class="mvp-tile">
                        <b>{{ tile.value }}</b>
                        <small>{{ tile.label }}</small>
                    </span>
                </div>
            </div>
        </div>
    </broadcast-card>

    <!-- pregame: the best player of each team, by average PIR this season -->
    <broadcast-card v-else eyebrow="Πριν τον αγώνα" title="Παίκτες που ξεχωρίζουν">
        <broadcast-compare :sides="watchSides" :rows="watchRows" />
        <div class="mvp-note">Μέσοι όροι ανά αγώνα στη σεζόν</div>
    </broadcast-card>
</template>

<script>
    import BroadcastCard from '@/components/BroadcastCard'
    import BroadcastCompare from '@/components/BroadcastCompare'
    import { getMatchColors } from '@/utils/colors'

    const ASSETS_URL = 'https://storage.googleapis.com'

    const WATCH_STATS = [
        { key: 'points', label: 'Πόντοι' },
        { key: 'two', label: 'Δίποντα', made: 'twoPointsMade', attempts: 'twoPointAttempts' },
        { key: 'three', label: 'Τρίποντα', made: 'threePointsMade', attempts: 'threePointAttempts' },
        { key: 'free', label: 'Βολές', made: 'freeThrowsMade', attempts: 'freeThrowAttempts' },
        { key: 'rebounds', label: 'Ριμπάουντ' },
        { key: 'assists', label: 'Ασίστ' },
        { key: 'steals', label: 'Κλεψίματα' },
        { key: 'turnovers', label: 'Λάθη', lowerIsBetter: true },
        { key: 'pir', label: 'PIR' },
        { key: 'playTimeInSecs', label: 'Λεπτά', minutes: true }
    ]

    export default {
        name: 'MvpComparison',
        components: { BroadcastCard, BroadcastCompare },
        props: {
            mvp: {
                type: Boolean,
                default: false
            },
            match: {
                type: Object,
                default: () => ({})
            },
            team1: {
                type: Object,
                default: () => ({})
            },
            team2: {
                type: Object,
                default: () => ({})
            },
            mvpPlayer: {
                type: Object,
                default: () => ({ stats: [{ pir: 0 }] })
            },
            // 1 | 2
            mvpTeam: {
                type: Number,
                default: null
            }
        },
        data() {
            return {
                defaultPlayerImg: require('@/assets/default_player.png')
            }
        },
        computed: {
            colors() {
                return getMatchColors(this.match.team1, this.match.team2)
            },
            mvpSide() {
                const key = this.mvpTeam === 2 ? 'team2' : 'team1'
                const team = this.match[key] || {}
                return { name: team.name, logo: team.logo ? `${ASSETS_URL}/${team.logo}` : null, color: this.colors[key] }
            },
            mvpStats() {
                return (this.mvpPlayer && this.mvpPlayer.stats && this.mvpPlayer.stats[0]) || {}
            },
            mvpTiles() {
                const s = this.mvpStats
                const n = key => Number(s[key]) || 0
                return [
                    { label: 'Πόντοι', value: n('points') },
                    { label: 'Ριμπάουντ', value: n('rebounds') },
                    { label: 'Ασίστ', value: n('assists') },
                    { label: 'Κλεψίματα', value: n('steals') },
                    { label: 'Κοψίματα', value: n('blockedShots') },
                    { label: 'Σουτ', value: `${n('twoPointsMade') + n('threePointsMade')}/${n('twoPointAttempts') + n('threePointAttempts')}` },
                    { label: 'Τρίποντα', value: `${n('threePointsMade')}/${n('threePointAttempts')}` },
                    { label: 'Βολές', value: `${n('freeThrowsMade')}/${n('freeThrowAttempts')}` }
                ]
            },
            // highest average PIR of each team
            watchPlayers() {
                return ['team1', 'team2'].map((key) => {
                    const players = (this[key] && this[key].players) || []
                    return players.reduce((best, player) => (!best || this.averagePir(player) > this.averagePir(best) ? player : best), null)
                })
            },
            watchSides() {
                return this.watchPlayers.map((player, index) => {
                    const key = index ? 'team2' : 'team1'
                    const team = this.match[key] || {}
                    return {
                        name: player ? this.playerName(player) : '—',
                        sub: team.name,
                        photo: player ? this.playerImage(player) : this.defaultPlayerImg,
                        logo: team.logo ? `${ASSETS_URL}/${team.logo}` : null,
                        color: this.colors[key]
                    }
                })
            },
            watchRows() {
                const [p1, p2] = this.watchPlayers.map(player => (player && player.avg) || {})
                return WATCH_STATS.map((stat) => {
                    if (stat.made) {
                        const pct = avg => (Number(avg[stat.attempts]) ? Math.round(Number(avg[stat.made]) / Number(avg[stat.attempts]) * 1000) / 10 : 0)
                        const a = pct(p1)
                        const b = pct(p2)
                        return { key: stat.key, label: stat.label, value1: a, value2: b, display1: `${a.toFixed(1)}%`, display2: `${b.toFixed(1)}%` }
                    }
                    const a = Number(p1[stat.key]) || 0
                    const b = Number(p2[stat.key]) || 0
                    const show = value => (stat.minutes ? this.minutes(value) : value.toFixed(1))
                    return { key: stat.key, label: stat.label, value1: a, value2: b, display1: show(a), display2: show(b), lowerIsBetter: stat.lowerIsBetter }
                })
            }
        },
        methods: {
            averagePir(player) {
                if (player.averagePir !== null && player.averagePir !== undefined) return Number(player.averagePir) || 0
                return Number(player.avg && player.avg.pir) || 0
            },
            playerName(player = {}) {
                const jersey = player.alternativeJersey ?? player.jersey
                return `${jersey !== null && jersey !== undefined ? `#${jersey} ` : ''}${player.name || ''} ${player.surname || ''}`.trim()
            },
            playerImage(player = {}) {
                return player.image ? `${ASSETS_URL}/${player.image}` : this.defaultPlayerImg
            },
            minutes(secs) {
                const value = Math.round(Number(secs) || 0)
                return `${Math.floor(value / 60)}:${String(value % 60).padStart(2, '0')}`
            }
        }
    }
</script>

<style scoped>
/* ---------- MVP ---------- */
.mvp {
    display: flex;
    align-items: center;
    gap: 40px;

    /* the logo behind the player stays inside the card */
    overflow: hidden;
    padding-left: 6px;
    border-left: 8px solid var(--team-color);
    border-radius: 6px;
}

.mvp__photo {
    position: relative;
    flex: 0 0 auto;
    width: 380px;
    height: 460px;
}

.mvp__watermark {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 130%;
    transform: translate(-50%, -50%);
    opacity: 0.14;
}

.mvp__img {
    position: relative;
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: bottom;
}

.mvp__info {
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: flex-start;
    min-width: 0;
}

.mvp__team {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    color: rgba(0, 0, 0, 0.55);
    font-size: 1.15rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.mvp__team img {
    width: 40px;
    height: 40px;
    object-fit: contain;
}

.mvp__name {
    margin-top: 6px;
    font-family: Mansalva, Arial, sans-serif;
    font-size: 3rem;
    font-weight: 800;
    line-height: 1.15;
}

.mvp__pir {
    background-image: linear-gradient(90deg, #ef4444 0%, #ff7133 70%, orange 100%);
    background-clip: text;
    -webkit-text-fill-color: transparent;
    font-size: 5rem;
    font-weight: 800;
    line-height: 1.05;
}

.mvp__pir small {
    font-size: 1.8rem;
}

.mvp__tiles {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
    width: 100%;
    margin-top: 18px;
}

.mvp-tile {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 12px 8px;
    border-radius: 14px;
    background: #f4f4f6;
    line-height: 1.15;
}

.mvp-tile b {
    color: #111;
    font-size: 2rem;
    font-variant-numeric: tabular-nums;
}

.mvp-tile small {
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.95rem;
    font-weight: 700;
}

.mvp-note {
    margin-top: 10px;
    color: rgba(0, 0, 0, 0.45);
    font-size: 1rem;
    text-align: center;
}
</style>
